import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';

// Third party libraries
import Papa from 'papaparse';
import TruncateTextFixedLength from '~/components/General/TruncateTextFixedLength';
// Hooks
import { useDispatch, useSelector } from 'react-redux';

// Actions
import { failure, success } from '~/redux/actions/snackbar-actions';

// Utils
import { MESSAGES } from '~/utils/message';
import { setItem } from '~/utils/local-storage.js';
import request from '~/utils/request';

// MUI Component
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {
  Box,
  FormControl,
  Grid,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import Select from '~/components/General/OlcSelect';
import MenuItem from '@mui/material/MenuItem';
import CloseIcon from '@mui/icons-material/Close';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ListItemText from '@mui/material/ListItemText';
import DoneIcon from '@mui/icons-material/Done';

// Icons
import {
  VerifiedCIcon,
  NotVerifiedCIcon,
  VerifiiedIcon2,
  NotVerifiedCIcon2,
  CrossIconCIcon,
  UserImportIcon,
  CustomFieldImportIcon,
  HomeImportIcon,
  InfoCIconFill,
  WarningErrorImport,
} from '~/assets/images';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// Styles
import './styles.scss';
import ConfirmModal from '~/components/General/ConfirmModal';
import OlcButton from '~/components/General/OlcButton';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 230,
      scrollbarWidth: 'thin',
    },
  },
};

const MappedContacts = (props) => {
  const {
    selectedFile,
    setRequestId,
    setProcessCsv,
    onImportComplete,
    onBack,
    selectedTags = [], // New prop for selected tags
    selectedTagIds = [], // New prop for selected tag IDs
    isDncList = false,
    isOrdersContext = false,
    processFileSignal,
    onProgressStateChange,
  } = props;

  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mappingFileds, setMappingFileds] = useState([]);
  const [mappingOptions, setMappingOptions] = useState([]);
  const [search, setSearch] = useState('');
  const [customValue, setCustomValue] = useState('');
  const [loadOffers, setLoadOffers] = useState(false);
  const [offerPercentage, setOfferPercentage] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [sameAsMailingAddress, setSameAsMailingAddress] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const selectRefs = useRef({});
  const prevPropertyMappingRef = useRef(null);

  const isPropertyAddressEnabled = useSelector(
    (state) =>
      state?.generalSettingsReducers?.generalSettings?.propertyAddressStrictness === 'Include' ||
      false
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  // Bubble mapping/validation errors to Orders footer
  useEffect(() => {
    if (!isOrdersContext || typeof onProgressStateChange !== 'function') return;
    const hasError = Array.isArray(errors) && errors.length > 0;
    onProgressStateChange({
      errorMessage: hasError ? 'Correct errors above before proceeding' : '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  // Process CSV file and get mapping suggestions
  const processCsvFile = async () => {
    setLoading(true);

    if (!selectedFile || selectedFile.length === 0) {
      setErrors(['Please select a file first']);
      setLoading(false);
      return;
    }

    Papa.parse(selectedFile[0], {
      header: true,
      skipEmptyLines: true,
      worker: true,
      complete: async function (results) {
        const rowsArray = [];
        const valuesArray = [];

        // Iterating data to get column name and their values
        results.data.map((d) => {
          rowsArray.push(Object.keys(d));
          valuesArray.push(Object.values(d));
        });

        const headerRow = rowsArray[0];
        const dataRows = results.data.slice(0, 2);

        if (!headerRow?.length || !dataRows?.length) {
          setErrors(['The uploaded file is empty or invalid']);
          setLoading(false);
          return;
        }

        const headerOptions = [];
        headerRow.forEach((header) => {
          headerOptions.push({
            name: header.trim(),
            examples: dataRows.map((row) => row[header]),
          });
        });

        try {
          const exportHeaders = await request.post('contacts/import/getSuggestions', {
            headers: headerRow.toString(),
          });

          const suggestion = [];
          suggestion.push(exportHeaders.data.data);
          const autoSuggestion = suggestion[0]['auto_suggested'];

          // Combine contact_fields and custom_fields
          const combinedFields = suggestion.map((item) => {
            return [
              ...item.contact_fields,
              ...item.custom_fields.map((field) => ({
                label: field,
                field: field,
                required: false,
                isPropertyField: false,
              })),
            ];
          });

          // Add custom fields that are always needed
          const customFields = [];

          const allFields = [...combinedFields[0], ...customFields];

          const alwaysRequiredLabels = new Set([
            'Mailing Address 1',
            'Mailing City',
            'Mailing State',
            'Mailing Zip Code',
          ]);

          const mappedFileds = allFields
            .map((item) => {
              // Allow auto-suggestion for all fields, including property address
              const matchingKey = Object.entries(autoSuggestion).find(
                ([key, value]) => value === item.field
              );

              return {
                label: item.label,
                dbColumn: item.field || item.label,
                value: matchingKey?.length ? matchingKey[0] : '',
                required: item.required || alwaysRequiredLabels.has(item.label) || false,
                error: false,
                display: item.isPropertyField
                  ? item.isPropertyField && isPropertyAddressEnabled
                  : true,
              };
            })
            .filter((item) => item.display !== false);

          setMappingOptions(headerOptions);
          setMappingFileds(mappedFileds);
        } catch (error) {
          dispatch(
            failure(error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR)
          );
        } finally {
          setLoading(false);
        }
      },
      error: () => {
        setErrors(['The uploaded file could not be read']);
        setLoading(false);
      },
    });
  };

  // Process CSV file when component mounts or file changes
  useEffect(() => {
    if (selectedFile && selectedFile.length > 0) {
      processCsvFile();
    }
  }, [selectedFile]);

  const handleChange = (params, event) => {
    let updatedData = mappingFileds.map((item) => {
      if (item.label === params) {
        return { ...item, value: event.target.value, error: false };
      }
      return item;
    });

    // Special case: If either First Name or Company now has a value, clear both errors
    if (params === 'First Name' || params === 'Company') {
      const firstNameField = updatedData.find((i) => i.label === 'First Name');
      const companyField = updatedData.find((i) => i.label === 'Company');
      if ((firstNameField && firstNameField.value) || (companyField && companyField.value)) {
        updatedData = updatedData.map((item) => {
          if (item.label === 'First Name' || item.label === 'Company') {
            return { ...item, error: false };
          }
          return item;
        });
      }
    }

    // Update errors list by removing the fixed field, and also handle First Name/Company coupling
    setErrors((prev) => {
      let next = prev.filter((e) => !e.includes(params));
      if (params === 'First Name' || params === 'Company') {
        next = next.filter((e) => !e.includes('First Name') && !e.includes('Company'));
      }
      return next;
    });

    setMappingFileds(updatedData);
    setSearch('');
    setActiveDropdown(null);
    setOpenDropdowns((prev) => ({ ...prev, [params]: false }));
  };

  const handleClearSelection = (params) => {
    const clearSelction = mappingFileds.map((item) => {
      if (item.label === params) {
        return { ...item, value: '' };
      }
      return item;
    });
    setMappingFileds(clearSelction);
    setSearch('');
    setActiveDropdown(null);
    setOpenDropdowns((prev) => ({ ...prev, [params]: false }));
  };

  const handleBack = () => {
    setSearch('');
    setMappingFileds([]);
    setMappingOptions([]);
    setErrors([]);
    setLoadOffers(false);
    setOfferPercentage(false);
    setActiveDropdown(null);
    setOpenDropdowns({});
    setSameAsMailingAddress(false);
    if (onBack) {
      onBack();
    }
  };

  const validateFields = () => {
    let inValids = [];
    const validatedData = mappingFileds.map((item) => {
      if (item.required && !item.value) {
        inValids.push(item.label);
        return { ...item, error: true };
      }
      return item;
    });

    // Check if First Name or Company is provided
    const firstNameField = validatedData.find((item) => item.label === 'First Name');
    const companyField = validatedData.find((item) => item.label === 'Company');

    const hasFirstName = firstNameField && firstNameField.value;
    const hasCompany = companyField && companyField.value;

    // If either First Name or Company is provided, remove both from inValids
    let finalData = validatedData;
    if (hasFirstName || hasCompany) {
      inValids = inValids.filter((item) => item !== 'First Name' && item !== 'Company');

      // Also remove error states from both fields
      const updatedData = validatedData.map((item) => {
        if (item.label === 'First Name' || item.label === 'Company') {
          return { ...item, error: false };
        }
        return item;
      });
      finalData = updatedData;
    } else {
      // If neither is provided, show error for at least one
      if (inValids.includes('First Name') || inValids.includes('Company')) {
        inValids = inValids.filter((item) => item !== 'First Name' && item !== 'Company');
        inValids.unshift('First Name or Company');

        const updatedData = validatedData.map((item) => {
          if (item.label === 'First Name' || item.label === 'Company') {
            return { ...item, error: true };
          }
          return item;
        });
        finalData = updatedData;
      }
    }

    if (inValids.length) {
      setMappingFileds(finalData);
      setErrors(inValids);
      return false;
    } else {
      setMappingFileds(finalData);
      setErrors([]);
      return true;
    }
  };

  const handleExport = async () => {
    try {
      const isValid = validateFields();
      if (isValid) {
        // Exclude property address fields entirely if the feature is disabled
        const fieldsForExport = !isPropertyAddressEnabled
          ? mappingFileds.filter(
              (item) =>
                ![
                  'Property Address',
                  'Property City',
                  'Property State',
                  'Property Zip Code',
                ].includes(item.label)
            )
          : mappingFileds;

        let headers = fieldsForExport.map((item) => {
          return {
            dbColumn: item.dbColumn,
            csvColumn: item.value,
          };
        });

        setLoading(true);
        const form = new FormData();
        form.append('headers', JSON.stringify(headers));
        form.append('csv', selectedFile[0]);

        // Add tags data to the form - ensure proper formatting
        const tagIdsString =
          selectedTagIds && selectedTagIds.length > 0 ? selectedTagIds.join(',') : '';

        // Add tags data to form - try both field names to ensure compatibility
        if (tagIdsString) {
          form.append('tags', tagIdsString);
          // form.append('tagIds', tagIdsString); // Also add as tagIds for backward compatibility
        }
        form.append('isDncList', isDncList.toString());

        const response = await request.post('contacts/processCsv', form);

        setRequestId(response.data.data.reqId);
        setItem('reqId', response.data.data.reqId);
        setProcessCsv((prev) => ({
          ...prev,
          current: response.data.data.current,
          processing: true,
          total: response.data.data.total,
        }));

        dispatch(success(response.data.message));

        // Call the onImportComplete callback if provided
        if (onImportComplete) {
          onImportComplete({
            ...response.data,
            selectedTags,
            selectedTagIds,
            isDncList,
          });
        }
      }
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR));
      return error;
    } finally {
      setLoading(false);
    }
  };

  const handleOfferPercentageChange = (e) => {
    const value = e.target.value;
    if (value >= 1 && value <= 100) {
      setCustomValue(value);
    } else if (value < 1) {
      setCustomValue('');
    }
  };

  // Trigger export from main footer in Orders context
  const lastSignalRef = useRef(processFileSignal);
  useEffect(() => {
    if (processFileSignal !== undefined && processFileSignal !== lastSignalRef.current) {
      lastSignalRef.current = processFileSignal;
      if (!loading) {
        handleExport();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processFileSignal]);

  // Filter options based on search term
  const filteredOptions = mappingOptions.filter((option) =>
    option.name.toLowerCase().includes(search.toLowerCase())
  );

  // Sample data for offer percentage table
  const data = [
    {
      address: '4517 Girvan Dr #e Myrtle Beach SC 29579',
      offers: ['Call for Offer', '$165,514', '$173,859', '$188,370', 'Call for Offer'],
    },
    {
      address: '2313 S Robeson Ave Florence SC 29505',
      offers: ['$104,650', '$165,514', '$173,859', '$188,370', 'Call for Offer'],
    },
    {
      address: '2313 S Robeson Ave Florence SC 29505',
      offers: ['$104,650', '$165,514', '$173,859', '$188,370', 'Call for Offer'],
    },
  ];

  const handleBackToUpload = () => {
    if (onBack) {
      onBack();
    }
  };

  // Dynamic category objects based on API response
  const categories = [
    {
      id: 'contact-info',
      title: 'Contact Information',
      icon: <img src={UserImportIcon} style={{ lineHeight: '0' }} />, // Person icon - will be replaced with proper icon component
      fields: [
        'First Name',
        'Last Name',
        'Company',
        'Contact Phone Number',
        'Contact Email',
        'Tags',
      ],
    },
    {
      id: 'mailing-address',
      title: 'Mailing Address',
      icon: <img src={HomeImportIcon} style={{ lineHeight: '0' }} />, // House icon - will be replaced with proper icon component
      fields: [
        'Mailing Address 1',
        'Mailing Address 2',
        'Mailing City',
        'Mailing State',
        'Mailing Zip Code',
      ],
    },
    {
      id: 'property-address',
      title: 'Property Address',
      text: 'Mapping property address fields to existing contacts will overwrite their current data.',
      icon: <img src={HomeImportIcon} style={{ lineHeight: '0' }} />, // House icon - will be replaced with proper icon component
      fields: ['Property Address', 'Property City', 'Property State', 'Property Zip Code'],
    },
    {
      id: 'custom-fields',
      title: 'Custom Fields',
      text: 'Mapping custom fields to existing contacts will overwrite their current data.',
      icon: <img src={CustomFieldImportIcon} style={{ lineHeight: '0' }} />, // House icon - will be replaced with proper icon component
      fields: mappingFileds
        .filter(
          (field) =>
            ![
              'First Name',
              'Last Name',
              'Company',
              'Contact Phone Number',
              'Contact Email',
              'Tags',
              'Mailing Address 1',
              'Mailing Address 2',
              'Mailing City',
              'Mailing State',
              'Mailing Zip Code',
              'Property Address',
              'Property City',
              'Property State',
              'Property Zip Code',
            ].includes(field.label) && !field.isPropertyField
        )
        .map((field) => field.label),
    },
  ];

  // Group mapping fields by category
  const groupedFields = categories.map((category) => ({
    ...category,
    fields: mappingFileds.filter((field) => category.fields.includes(field.label)),
  }));

  // Handle same as mailing address checkbox
  const handleSameAsMailingAddressChange = (event) => {
    const checked = event.target.checked;
    setSameAsMailingAddress(checked);

    if (checked) {
      // cache previous property field mappings so we can restore on uncheck
      const cache = {};
      ['Property Address', 'Property City', 'Property State', 'Property Zip Code'].forEach(
        (label) => {
          const found = mappingFileds.find((f) => f.label === label);
          cache[label] = found ? found.value : '';
        }
      );
      prevPropertyMappingRef.current = cache;

      const updated = mappingFileds.map((field) => {
        if (field.label === 'Property Address') {
          const mailingAddress1 = mappingFileds.find((f) => f.label === 'Mailing Address 1');
          return { ...field, value: mailingAddress1?.value || '' };
        }
        if (field.label === 'Property City') {
          const city = mappingFileds.find((f) => f.label === 'Mailing City');
          return { ...field, value: city?.value || '' };
        }
        if (field.label === 'Property State') {
          const state = mappingFileds.find((f) => f.label === 'Mailing State');
          return { ...field, value: state?.value || '' };
        }
        if (field.label === 'Property Zip Code') {
          const zipCode = mappingFileds.find((f) => f.label === 'Mailing Zip Code');
          return { ...field, value: zipCode?.value || '' };
        }
        return field;
      });
      setMappingFileds(updated);
    } else {
      // restore previous property mappings if we cached them
      if (prevPropertyMappingRef.current) {
        const previous = prevPropertyMappingRef.current;
        const restored = mappingFileds.map((field) => {
          if (previous[field.label] !== undefined) {
            return { ...field, value: previous[field.label] };
          }
          return field;
        });
        setMappingFileds(restored);
        prevPropertyMappingRef.current = null;
      }
    }
  };

  return (
    <>
      <Box
        className="dragDropContainer"
        sx={{
          py: 0,
          px: 2,
          marginTop: '24px',
          height: 'calc(100vh - 200px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Main Content - Categories */}
        <Box
          className="mainContent"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: 0, // Allow flex item to shrink below content size
            borderRadius: '12px',
            // Debug: temporary red background
          }}
        >
          {!loadOffers && (
            <Box
              className="categoriesContainer"
              sx={{
                pl: 5,
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              {loading && mappingFileds.length === 0 ? (
                <Box className="mappingLoadingState" role="status" aria-live="polite">
                  <CircularProgress size={32} />
                  <Typography>Preparing field mapping...</Typography>
                </Box>
              ) : (
                groupedFields.map((category, categoryIndex) => (
                  <Box key={category.id}>
                  {/* Category header */}
                  <Box display="flex" alignItems="center">
                    <Box mr={1} display="flex" alignItems="center">
                      {category.icon}
                    </Box>
                    <Typography className="categoryTitle">{category.title}</Typography>
                  </Box>

                  {/* Category text */}
                  {category.text &&
                    !(category.id === 'property-address' && !isPropertyAddressEnabled) && (
                      <Typography className="categoryText" style={{ marginTop: '8px' }}>
                        {category.text}
                      </Typography>
                    )}

                  {/* Same as Mailing Address checkbox for property-address */}
                  {category.id === 'property-address' && isPropertyAddressEnabled && (
                    <Box
                      style={{
                        marginBottom: sameAsMailingAddress ? '20px' : '',
                        marginTop: '8px',
                      }}
                    >
                      <FormControlLabel
                        className="returnAddressCheck"
                        control={
                          <Box className="checkboxRow ">
                            <Checkbox
                              className="bulkCheck newbulkCheck checkBoxSm"
                              icon={<Box className="bulkNotchecked" />}
                              checkedIcon={<DoneIcon />}
                              checked={sameAsMailingAddress}
                              onChange={handleSameAsMailingAddressChange}
                            />
                          </Box>
                        }
                        label={
                          <Box style={{ display: 'flex', alignItems: 'center' }}>
                            <Typography className="categoryCheckboxText">
                              Same as Mailing Address
                            </Typography>
                            <Tooltip title="Check this if the mailing and property addresses are the same">
                              <Box style={{ marginLeft: '-2px', paddingTop: '5px' }}>
                                <img src={InfoCIconFill} alt="icon" />
                              </Box>
                            </Tooltip>
                          </Box>
                        }
                      />
                    </Box>
                  )}

                  {/* Table - Show empty state if property-address and not enabled, hide if checkbox is checked */}
                  {category.id === 'property-address' && !isPropertyAddressEnabled ? (
                    <Box className="emptyTabContent" sx={{ mb: 3, p: 3, textAlign: 'center' }}>
                      <Typography>
                        {MESSAGES.CONTACTS.PROPERTY_TAB.PROPERTY_TAB_DESCRIPTION}
                      </Typography>
                      <Link className="olcPrimaryLink" to="/my-company">
                        {MESSAGES.CONTACTS.PROPERTY_TAB.ENABLE_PROPERTY_ADDRESS}
                      </Link>
                    </Box>
                  ) : (
                    !(
                      category.id === 'property-address' &&
                      sameAsMailingAddress &&
                      isPropertyAddressEnabled
                    ) && (
                      <TableContainer
                        className="themeTable olcThemeTable"
                        component={Paper}
                        sx={{ mb: 3 }}
                      >
                        <Table className="mappedContactsTable" size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ width: '33%', paddingLeft: '76px' }}>
                                <span>OLC Fields</span>
                              </TableCell>
                              <TableCell sx={{ width: '33%' }}>CSV Columns</TableCell>
                              <TableCell sx={{ width: '34%' }}>Sample Data</TableCell>
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {category.fields.map((content, index) => {
                              const isMatched = content.value;
                              const isError =
                                content.error && errors.some((str) => str.includes(content.label));

                              return (
                                <TableRow key={index}>
                                  {/* OLC Fields with status */}
                                  <TableCell className="mappedOlcFieldCell">
                                    <Box
                                      className="mappedOlcFieldContent"
                                      display="flex"
                                      alignItems="center"
                                      gap={'32px'}
                                    >
                                      <img
                                        src={isMatched ? VerifiiedIcon2 : NotVerifiedCIcon2}
                                        alt={isMatched ? 'matched' : 'unmatched'}
                                        style={{ width: 18, height: 18, marginLeft: '8px' }}
                                      />
                                      {content.label?.length > 25 ? (
                                        <TruncatedText text={content.label} />
                                      ) : (
                                        <Typography className="mappedSampleData mappedOlcFieldLabel">
                                          {content.label}
                                          {content.required && content.label !== 'Company' && (
                                            <Box component="span" sx={{ color: 'red' }}>
                                              *
                                            </Box>
                                          )}
                                        </Typography>
                                      )}
                                    </Box>
                                  </TableCell>

                                  {/* CSV Columns dropdown */}
                                  <TableCell>
                                    <FormControl
                                      className={[
                                        isError
                                          ? 'errorOption selectorOption'
                                          : isMatched
                                            ? 'successOption selectorOption'
                                            : 'unSelectedOption selectorOption',
                                        openDropdowns[content.label] && 'menuOpen',
                                      ]
                                        .filter(Boolean)
                                        .join(' ')}
                                      sx={{
                                        width: '300px',
                                      }}
                                    >
                                      <Select
                                        ref={(el) => (selectRefs.current[content.label] = el)}
                                        value={content.value}
                                        open={openDropdowns[content.label] || false}
                                        onChange={(e) => handleChange(content.label, e)}
                                        className="searchInputImportContact"
                                        onOpen={() => {
                                          setActiveDropdown(content.label);
                                          setOpenDropdowns((prev) => ({
                                            ...prev,
                                            [content.label]: true,
                                          }));
                                        }}
                                        onClose={() => {
                                          setActiveDropdown(null);
                                          setSearch('');
                                          setOpenDropdowns((prev) => ({
                                            ...prev,
                                            [content.label]: false,
                                          }));
                                        }}
                                        displayEmpty
                                        renderValue={(selected) => {
                                          if (!selected) {
                                            return (
                                              <Typography
                                                className={
                                                  isError ? 'errorOption' : 'unSelectedOption'
                                                }
                                                sx={{
                                                  fontSize: '14px',
                                                  fontFamily: 'Inter',
                                                  fontWeight: 400,
                                                }}
                                              >
                                                {content.label}
                                              </Typography>
                                            );
                                          }
                                          return (
                                            <Typography
                                              className={
                                                isError
                                                  ? 'errorOption'
                                                  : isMatched
                                                    ? 'successOption'
                                                    : 'unSelectedOption'
                                              }
                                              sx={{
                                                fontSize: '14px',
                                                fontFamily: 'Inter',
                                                fontWeight: 400,
                                              }}
                                            >
                                              {selected}
                                            </Typography>
                                          );
                                        }}
                                        endAdornment={
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '4px',
                                              paddingRight: '4px',
                                              cursor: 'pointer',
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              // Toggle dropdown state
                                              setOpenDropdowns((prev) => ({
                                                ...prev,
                                                [content.label]: !prev[content.label],
                                              }));
                                              if (!openDropdowns[content.label]) {
                                                setActiveDropdown(content.label);
                                              } else {
                                                setActiveDropdown(null);
                                                setSearch('');
                                              }
                                            }}
                                          >
                                            {content.value && (
                                              <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleClearSelection(content.label);
                                                }}
                                                sx={{
                                                  padding: '2px',
                                                  color: 'rgba(176, 176, 176, 1)',
                                                  '&:hover': {
                                                    color: 'rgba(176, 176, 176, .5)',
                                                    backgroundColor: 'transparent',
                                                  },
                                                }}
                                              >
                                                <CloseIcon sx={{ fontSize: '16px' }} />
                                              </IconButton>
                                            )}
                                            <KeyboardArrowDownIcon
                                              sx={{
                                                fontSize: '20px',
                                                color: 'rgba(176, 176, 176, 1)',
                                                transform:
                                                  activeDropdown === content.label
                                                    ? 'rotate(180deg)'
                                                    : 'rotate(0deg)',
                                                transition: 'transform 0.2s ease-in-out',
                                              }}
                                            />
                                          </Box>
                                        }
                                        MenuProps={{
                                          ...MenuProps,
                                          disableAutoFocusItem: true,
                                          disableRestoreFocus: true,
                                          autoFocus: false,
                                          PaperProps: {
                                            sx: {
                                              marginTop: '4px',
                                            },
                                          },
                                          MenuListProps: {
                                            className: 'csvColumnMenuList',
                                          },
                                          classes: {
                                            paper: 'newDesignMenuPaper olcNewDesignMenuPaper',
                                          },
                                        }}
                                      >
                                        {activeDropdown === content.label && (
                                          <Box
                                            className="importScvSearchContainer"
                                            sx={{
                                              position: 'sticky',
                                              top: 0,
                                              backgroundColor: 'white',
                                              zIndex: 1,
                                              padding: '8px',
                                              borderBottom: '1px solid #E5E7EB',
                                            }}
                                          >
                                            <TextField
                                              placeholder="Search / Select CSV column"
                                              fullWidth
                                              size="small"
                                              value={search}
                                              onChange={(e) => setSearch(e.target.value)}
                                              autoComplete="off"
                                              onKeyDown={(e) => e.stopPropagation()}
                                              onFocus={(e) => e.stopPropagation()}
                                              onClick={(e) => e.stopPropagation()}
                                              InputProps={{
                                                endAdornment:
                                                  search.length > 0 ? (
                                                    <InputAdornment position="end">
                                                      <IconButton
                                                        size="small"
                                                        onClick={() => setSearch('')}
                                                        sx={{ padding: '2px' }}
                                                      >
                                                        <img
                                                          src={CrossIconCIcon}
                                                          alt="clear"
                                                          style={{ width: '14px', height: '14px' }}
                                                        />
                                                      </IconButton>
                                                    </InputAdornment>
                                                  ) : null,
                                              }}
                                              sx={{
                                                '& .MuiOutlinedInput-root': {
                                                  height: '36px',
                                                },
                                              }}
                                            />
                                          </Box>
                                        )}
                                        {filteredOptions.length > 0 ? (
                                          filteredOptions.map(({ name }, id) => (
                                            <MenuItem key={id} value={name}>
                                              <ListItemText primary={name} />
                                            </MenuItem>
                                          ))
                                        ) : search.length > 0 ? (
                                          <MenuItem disabled>
                                            <ListItemText
                                              primary="No columns to show"
                                              sx={{
                                                opacity: 0.6,
                                                textAlign: 'center',
                                              }}
                                            />
                                          </MenuItem>
                                        ) : (
                                          mappingOptions.map(({ name }, id) => (
                                            <MenuItem key={id} value={name}>
                                              <ListItemText primary={name} />
                                            </MenuItem>
                                          ))
                                        )}
                                      </Select>
                                    </FormControl>
                                  </TableCell>

                                  {/* Sample Data */}
                                  <TableCell>
                                    {mappingOptions.map((item, id) =>
                                      item.name.trim() === content.value ? (
                                        <Box key={id}>
                                          {item?.examples[0]?.length > 30 ? (
                                            <TruncateTextFixedLength
                                              text={item?.examples[0] || ''}
                                              maxLength={30}
                                            />
                                          ) : (
                                            <Typography className="mappedSampleData">
                                              {item.examples[0]}
                                            </Typography>
                                          )}

                                          {item?.examples[1]?.length > 30 ? (
                                            <TruncateTextFixedLength
                                              text={item?.examples[1] || ''}
                                              maxLength={30}
                                            />
                                          ) : (
                                            <Typography className="mappedSampleData">
                                              {item.examples[1]}
                                            </Typography>
                                          )}
                                        </Box>
                                      ) : null
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )
                  )}

                  {/* Divider between categories */}
                  {categoryIndex < groupedFields.length - 1 && (
                    <Box sx={{ width: '100%', height: '1px', bgcolor: '#E5E7EB' }} />
                  )}
                  </Box>
                ))
              )}
            </Box>
          )}

          {loadOffers && (
            <Box className="offerPercentageWrapper">
              <TableContainer>
                <Table className="offerTable">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {MESSAGES.CONTACTS.IMPORT.OFFER_PERCENTAGE_TABLE.OFFER_LEVEL}
                      </TableCell>
                      <TableCell>
                        {MESSAGES.CONTACTS.IMPORT.OFFER_PERCENTAGE_TABLE.OFFER_1}
                      </TableCell>
                      <TableCell>
                        {MESSAGES.CONTACTS.IMPORT.OFFER_PERCENTAGE_TABLE.OFFER_2}
                      </TableCell>
                      <TableCell>
                        {MESSAGES.CONTACTS.IMPORT.OFFER_PERCENTAGE_TABLE.OFFER_3}
                      </TableCell>
                      <TableCell>
                        {MESSAGES.CONTACTS.IMPORT.OFFER_PERCENTAGE_TABLE.OFFER_4}
                      </TableCell>
                      <TableCell>
                        {MESSAGES.CONTACTS.IMPORT.OFFER_PERCENTAGE_TABLE.CUSTOM}
                        <Box className="customPercentageWrapper">
                          <TextField
                            variant="outlined"
                            type="number"
                            className="customPercentageInput"
                            value={customValue}
                            onChange={handleOfferPercentageChange}
                          />
                          <span>%</span>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody className="offerTableBody">
                    {data.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.address}</TableCell>
                        {row.offers.map((offer, idx) => (
                          <TableCell key={idx}>
                            {idx === 4 ? (customValue ? '200CR' : 'Call for Offer') : offer}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>

        {!isOrdersContext && (
          <Box className="finishFooterWrapperStep2">
            <Box className="finishFooter importFlowFooter" style={{ marginTop: 'auto' }}>
              <Box className="footerLeft">
                <Button className="backLink" onClick={handleBackToUpload}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="10"
                    viewBox="0 0 14 10"
                    fill="none"
                  >
                    <path
                      d="M5 9L1 5M1 5L5 1M1 5L13 5"
                      stroke="black"
                      stroke-opacity="0.6"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  Back to Uploading
                </Button>

                <Box>
                  {errors && errors.length > 0 && (
                    <Box
                      className="errorMessageWrapperImportContactStep2"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginRight: { xs: 0, md: '24px' },
                        justifyContent: { xs: 'center', md: 'flex-start' },
                        order: { xs: 2, md: 1 },
                      }}
                    >
                      <img
                        src={WarningErrorImport}
                        alt="warning"
                        style={{ width: '16.67px', height: '15px' }}
                      />
                      <Typography
                        sx={{
                          color: '#E10606',
                          fontSize: '16px',
                          fontFamily: 'Inter',
                          fontWeight: 400,
                          lineHeight: 'normal',
                          textAlign: { xs: 'center', md: 'left' },
                        }}
                      >
                        {errors[0] ? 'Correct errors above before proceeding' : ''}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              <Box className="footerRight">
                <OlcButton
                  variant="secondary"
                  disabled={loading}
                  onClick={() => setIsCancelModalOpen(true)}
                >
                  Cancel
                </OlcButton>

                <OlcButton
                  variant="primary"
                  disabled={loading}
                  onClick={
                    !offerPercentage
                      ? handleExport
                      : () => {
                          setLoadOffers(true);
                        }
                  }
                >
                  {loading ? (
                    <CircularProgress
                      sx={{
                        color: 'white',
                        width: '25px !important',
                        height: '25px !important',
                      }}
                    />
                  ) : (
                    'Process File'
                  )}
                </OlcButton>
              </Box>
            </Box>

            {/* Action Buttons */}
            {/* <Box
            sx={{
              display: 'flex',
              gap: '12px',
              width: { xs: '100%', md: 'auto' },
              order: { xs: 1, md: 2 },
            }}
          >
            {!loadOffers && (
              <OlcButton
                disabled={loading}
                variant="primary"
                sx={{
                  width: { xs: '50%', md: '214px' },
                  height: '50px',
                }}
                onClick={
                  !offerPercentage
                    ? handleExport
                    : () => {
                        setLoadOffers(true);
                      }
                }
              >
                {loading ? (
                  <CircularProgress
                    sx={{
                      color: 'white',
                      width: '25px !important',
                      height: '25px !important',
                    }}
                  />
                ) : (
                  'Process File'
                )}
              </OlcButton>
            )}
             */}
          </Box>
        )}
      </Box>

      {/* Action Buttons and Error Messages - Outside the mappedContactContainer */}
      <ConfirmModal
        openConfirmModal={{
          open: isCancelModalOpen,
          header: 'Cancel Import?',
          message:
            'Are you sure you want to cancel this import? If you cancel now, your progress will be lost and no contacts will be imported.',
          component: <CancelOutlinedIcon />,
          containedIcon: true,
          successButtonName: 'Yes',
          cancelButtonName: 'No',
          handleOk: () => {
            setIsCancelModalOpen(false);
            navigate(
              window?.location?.search?.includes('origin=contacts')
                ? '/contacts'
                : '/account/imports-exports'
            );
          },
          handleCancelBtn: () => {
            setIsCancelModalOpen(false);
          },
          handleClose: () => {
            setIsCancelModalOpen(false);
          },
        }}
      />
    </>
  );
};

export default MappedContacts;
